import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  Crown,
  Download,
  LoaderCircle,
  MoveHorizontal,
  Pencil,
  RefreshCw,
  Search,
  Share2,
  Trophy,
  UserRound,
  UsersRound,
  X,
  createIcons,
} from "lucide";
import {
  AVATARS,
  DEFAULT_AVATAR_ID,
  getAvatarById,
  validateAvatarId,
  type AvatarId,
} from "../shared/avatars";
import {
  CHOICE_LABELS,
  QUESTIONS,
  QUIZ_LENGTH,
  createQuestionSet,
  getQuestionById,
  validateQuestionSet,
} from "../shared/questions";
import {
  filterQuestions,
  normalizeQuestionQuery,
} from "../shared/question-bank";
import { formatScore } from "../shared/scoring";
import {
  averageScore,
  compareWithAverage,
  identityTitle,
  layoutSpectrum,
  scoreToPercentages,
} from "../shared/presentation";
import type {
  LeaderboardEntry,
  LeaderboardResponse,
  SubmitResponse,
} from "../shared/types";
import { ApiError, getLeaderboard, submitAnswers, updateAvatar } from "./api";
import "./styles.css";

type View =
  | "home"
  | "quiz"
  | "confirm"
  | "result"
  | "leaderboard"
  | "questionBank";

interface Draft {
  version: 2;
  nickname: string;
  avatarId: AvatarId;
  questionIds: number[];
  answers: Array<number | null>;
  questionIndex: number;
}

interface LastResultAccess {
  nickname: string;
  submittedAt: string;
  editToken: string | null;
}

type AvatarScope = "draft" | "result";

interface AppState extends Draft {
  view: View;
  returnView: View;
  leaderboard: LeaderboardResponse;
  leaderboardLoading: boolean;
  leaderboardError: string;
  homeError: string;
  submitError: string;
  submitting: boolean;
  result: LeaderboardEntry | null;
  selectedMember: LeaderboardEntry | null;
  shareNotice: string;
  resultEditToken: string | null;
  avatarEditorOpen: boolean;
  avatarUpdateSaving: boolean;
  avatarUpdateError: string;
  pendingAvatarId: AvatarId | null;
  questionSearch: string;
}

const DRAFT_KEY = "pj-rank-draft";
const LAST_RESULT_KEY = "pj-rank-last-result";
const AVATAR_HINT_KEY = "pj-rank-avatar-hint-seen";
const DRAFT_VERSION = 2;
const AUTO_ADVANCE_DELAY_MS = 300;
const SPECTRUM_MAX_LANES = 20;
let autoAdvanceTimer: number | undefined;
let avatarScrollFrame: number | undefined;
let avatarHapticTimer: number | undefined;
let avatarHintTimer: number | undefined;
let avatarGestureActive = false;
let showAvatarHint = localStorage.getItem(AVATAR_HINT_KEY) !== "true";

function getAppRoot(): HTMLDivElement {
  const root = document.querySelector<HTMLDivElement>("#app");
  if (!root) {
    throw new Error("App root was not found.");
  }
  return root;
}
const app = getAppRoot();

function emptyAnswers(): Array<number | null> {
  return Array.from({ length: QUIZ_LENGTH }, () => null);
}

function restoreDraft(): Draft {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) throw new Error("No draft");
    const parsed = JSON.parse(raw) as Partial<Draft>;
    const nickname = typeof parsed.nickname === "string" ? parsed.nickname : "";
    const avatarId = validateAvatarId(parsed.avatarId)
      ? parsed.avatarId
      : DEFAULT_AVATAR_ID;
    if (parsed.version !== DRAFT_VERSION) {
      return {
        version: DRAFT_VERSION,
        nickname,
        avatarId,
        questionIds: [],
        answers: emptyAnswers(),
        questionIndex: 0,
      };
    }
    const questionIds = parsed.questionIds;
    if (!validateQuestionSet(questionIds)) {
      return {
        version: DRAFT_VERSION,
        nickname,
        avatarId,
        questionIds: [],
        answers: emptyAnswers(),
        questionIndex: 0,
      };
    }
    const answers = Array.isArray(parsed.answers)
      ? parsed.answers.map((answer) =>
          Number.isInteger(answer) && Number(answer) >= 0 && Number(answer) <= 6
            ? Number(answer)
            : null,
        )
      : [];
    if (answers.length !== QUIZ_LENGTH) throw new Error("Invalid answers");
    return {
      version: DRAFT_VERSION,
      nickname,
      avatarId,
      questionIds,
      answers,
      questionIndex: Math.min(
        Math.max(Number(parsed.questionIndex) || 0, 0),
        QUIZ_LENGTH - 1,
      ),
    };
  } catch {
    return {
      version: DRAFT_VERSION,
      nickname: "",
      avatarId: DEFAULT_AVATAR_ID,
      questionIds: [],
      answers: emptyAnswers(),
      questionIndex: 0,
    };
  }
}

const draft = restoreDraft();
const state: AppState = {
  ...draft,
  view: "home",
  returnView: "home",
  leaderboard: { total: 0, entries: [] },
  leaderboardLoading: true,
  leaderboardError: "",
  homeError: "",
  submitError: "",
  submitting: false,
  result: null,
  selectedMember: null,
  shareNotice: "",
  resultEditToken: null,
  avatarEditorOpen: false,
  avatarUpdateSaving: false,
  avatarUpdateError: "",
  pendingAvatarId: null,
  questionSearch: "",
};

function escapeHtml(value: string): string {
  return value.replace(
    /[&<>'"]/g,
    (character) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        "'": "&#39;",
        '"': "&quot;",
      })[character] ?? character,
  );
}

function persistDraft(): void {
  sessionStorage.setItem(
    DRAFT_KEY,
    JSON.stringify({
      version: DRAFT_VERSION,
      nickname: state.nickname,
      avatarId: state.avatarId,
      questionIds: state.questionIds,
      answers: state.answers,
      questionIndex: state.questionIndex,
    } satisfies Draft),
  );
}

function clearDraft(): void {
  sessionStorage.removeItem(DRAFT_KEY);
}

function readLastResultAccess(): LastResultAccess | null {
  try {
    const raw = localStorage.getItem(LAST_RESULT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (
      typeof parsed.nickname !== "string" ||
      typeof parsed.submittedAt !== "string"
    ) {
      throw new Error("Invalid result access");
    }
    return {
      nickname: parsed.nickname,
      submittedAt: parsed.submittedAt,
      editToken: typeof parsed.editToken === "string" ? parsed.editToken : null,
    };
  } catch {
    localStorage.removeItem(LAST_RESULT_KEY);
    return null;
  }
}

function persistLastResultAccess(
  result: LeaderboardEntry,
  editToken: string,
): void {
  localStorage.setItem(
    LAST_RESULT_KEY,
    JSON.stringify({
      nickname: result.nickname,
      submittedAt: result.submittedAt,
      editToken,
    } satisfies LastResultAccess),
  );
}

function resetForNextPerson(): void {
  state.nickname = "";
  state.avatarId = DEFAULT_AVATAR_ID;
  state.questionIds = [];
  state.answers = emptyAnswers();
  state.questionIndex = 0;
  state.result = null;
  state.homeError = "";
  state.submitError = "";
  state.resultEditToken = null;
  state.avatarEditorOpen = false;
  state.avatarUpdateSaving = false;
  state.avatarUpdateError = "";
  state.pendingAvatarId = null;
  clearDraft();
  localStorage.removeItem(LAST_RESULT_KEY);
}

function answersComplete(): boolean {
  return (
    validateQuestionSet(state.questionIds) &&
    state.answers.every((answer) => answer !== null)
  );
}

function scoreTone(score: number): "p" | "middle" | "j" {
  if (score <= -20) return "p";
  if (score >= 20) return "j";
  return "middle";
}

function renderIconButton(
  icon: string,
  label: string,
  action: string,
): string {
  return `<button class="icon-button" type="button" data-action="${action}" aria-label="${label}" title="${label}"><i data-lucide="${icon}" aria-hidden="true"></i></button>`;
}

function renderAvatar(
  avatarId: AvatarId | null,
  nickname: string,
  className = "member-avatar",
): string {
  const avatar = getAvatarById(avatarId);
  if (!avatar) {
    return `<span class="${className} avatar-placeholder" role="img" aria-label="${escapeHtml(nickname)} 暂未选择头像"><i data-lucide="user-round" aria-hidden="true"></i></span>`;
  }
  return `<img class="${className}" src="${avatar.src}" width="256" height="256" alt="${escapeHtml(nickname)} 的头像" loading="lazy" />`;
}

function renderAvatarPicker(
  selectedAvatarId = state.avatarId,
  scope: AvatarScope = "draft",
): string {
  const showHint = scope === "draft" && showAvatarHint;
  return `
    <fieldset class="avatar-picker avatar-picker-${scope}" data-avatar-stage data-avatar-scope="${scope}" aria-label="选择头像">
      ${
        showHint
          ? '<span class="avatar-gesture-hint" data-avatar-hint aria-hidden="true"><i data-lucide="move-horizontal"></i></span>'
          : ""
      }
      <div class="avatar-carousel-shell">
        <button class="carousel-button" type="button" data-action="avatar-previous" data-avatar-scope="${scope}" aria-label="上一个头像" title="上一个头像">
          <i data-lucide="chevron-left" aria-hidden="true"></i>
        </button>
        <div class="avatar-carousel" data-avatar-carousel data-avatar-scope="${scope}" aria-label="左右滑动选择头像">
          ${AVATARS.map(
            (avatar) => `
              <label class="avatar-option" data-avatar-id="${avatar.id}">
                <input
                  type="radio"
                  name="${scope}-avatar"
                  value="${avatar.id}"
                  aria-label="${avatar.name}"
                  ${selectedAvatarId === avatar.id ? "checked" : ""}
                />
                <span class="avatar-card">
                  <img src="${avatar.src}" width="256" height="256" alt="" />
                </span>
              </label>
            `,
          ).join("")}
        </div>
        <button class="carousel-button" type="button" data-action="avatar-next" data-avatar-scope="${scope}" aria-label="下一个头像" title="下一个头像">
          <i data-lucide="chevron-right" aria-hidden="true"></i>
        </button>
      </div>
    </fieldset>
  `;
}

function renderHeader(): string {
  return `
    <header class="site-header">
      <div class="header-inner">
        <button class="wordmark" type="button" data-action="home" aria-label="返回首页">
          <span>花儿与少年（</span><span class="wordmark-p">p</span><span class="wordmark-j">j</span><span>版）</span>
        </button>
        <div class="header-actions">
          ${renderIconButton("share-2", "分享链接", "share")}
          ${renderIconButton("book-open", "查看题库", "question-bank")}
          ${renderIconButton("trophy", "查看排行榜", "leaderboard")}
        </div>
      </div>
      <div class="pj-stripe" aria-hidden="true"><span></span><span></span><span></span></div>
    </header>
  `;
}

function renderStatus(): string {
  if (!state.shareNotice) return "";
  return `<div class="toast" role="status">${escapeHtml(state.shareNotice)}</div>`;
}

function renderShell(content: string): string {
  return `
    ${renderHeader()}
    <main id="main-content">${content}</main>
    <footer class="site-footer">
      <p>群聊娱乐测试，不代表正式 MBTI 结果。</p>
    </footer>
    ${renderStatus()}
  `;
}

function renderLoadingCards(count = 3): string {
  return Array.from(
    { length: count },
    () => `
      <div class="leader-card skeleton-card" aria-hidden="true">
        <span class="skeleton skeleton-rank"></span>
        <span class="skeleton skeleton-avatar"></span>
        <span class="skeleton skeleton-name"></span>
        <span class="skeleton skeleton-track"></span>
      </div>
    `,
  ).join("");
}

function renderSpectrum(score: number, compact = false): string {
  const tone = scoreTone(score);
  const readable = `${score <= -20 ? "偏 P" : score >= 20 ? "偏 J" : "弹性区"}，${formatScore(score)}`;
  return `
    <div class="spectrum ${compact ? "spectrum-compact" : ""}">
      <div class="spectrum-labels" aria-hidden="true">
        <span class="label-p">P</span>
        ${compact ? "" : '<span class="label-middle">弹性区</span>'}
        <span class="label-j">J</span>
      </div>
      <input
        class="spectrum-range tone-${tone}"
        type="range"
        min="-100"
        max="100"
        value="${score}"
        tabindex="-1"
        aria-label="${readable}"
        disabled
      />
      ${
        compact
          ? ""
          : '<div class="spectrum-ticks" aria-hidden="true"><span>-100</span><span>-20</span><span>20</span><span>+100</span></div>'
      }
    </div>
  `;
}

function renderGroupSpectrum(entries: readonly LeaderboardEntry[]): string {
  if (entries.length === 0) return "";
  const points = layoutSpectrum(
    entries,
    Math.min(entries.length, SPECTRUM_MAX_LANES),
    36,
  );
  const laneCount = Math.max(...points.map(({ lane }) => lane + 1));
  const averagePosition = Math.round(
    scoreToPercentages(averageScore(entries)).j / 5,
  ) * 5;
  return `
    <section class="group-spectrum" aria-labelledby="group-spectrum-title">
      <div class="group-spectrum-heading">
        <div>
          <p class="eyebrow">全群分布</p>
          <h2 id="group-spectrum-title">大家站在哪里</h2>
        </div>
        <span class="average-key"><span aria-hidden="true"></span>群平均</span>
      </div>
      <div class="spectrum-cloud spectrum-lanes-${laneCount}" aria-label="群友在 P 到 J 光谱上的分布">
        <div class="group-axis" aria-hidden="true"></div>
        <span class="group-average" data-position="${averagePosition}" aria-hidden="true"></span>
        ${points
          .map(({ entryIndex, lane, position }) => {
            const entry = entries[entryIndex];
            if (!entry) return "";
            const bucket = Math.round(position / 5) * 5;
            return `
              <button
                class="group-point spectrum-lane-${lane}"
                type="button"
                data-action="member-result"
                data-entry-index="${entryIndex}"
                data-lane="${lane}"
                data-position="${bucket}"
                aria-label="查看 ${escapeHtml(entry.nickname)} 的简短结果"
                title="${escapeHtml(entry.nickname)} · ${escapeHtml(entry.tendency)}"
              ><span class="group-point-label">${escapeHtml(entry.nickname)}</span></button>
            `;
          })
          .join("")}
        <span class="group-axis-label group-axis-p">P</span>
        <span class="group-axis-label group-axis-j">J</span>
      </div>
    </section>
  `;
}

function renderLeaderboardList(
  entries: readonly LeaderboardEntry[],
  currentEntry: LeaderboardEntry | null,
): string {
  const currentKey = currentEntry
    ? `${currentEntry.nickname}|${currentEntry.submittedAt}`
    : "";

  const cards = entries
    .map((entry, entryIndex) => {
      const key = `${entry.nickname}|${entry.submittedAt}`;
      const isCurrent = key === currentKey;
      const isMostP = entry.rank === 1;
      return `
        <article class="leader-card ${isCurrent ? "leader-card-current" : ""} ${isMostP ? "leader-card-winner" : ""}" data-testid="leaderboard-row">
          <button class="leader-card-main" type="button" data-action="member-result" data-entry-index="${entryIndex}">
            <span class="rank-number ${isMostP ? "rank-winner" : ""}" ${isMostP ? 'aria-label="第 1 名，最 P 之人"' : ""}>
              ${isMostP ? '<i data-lucide="crown" aria-hidden="true"></i>' : ""}<span>${String(entry.rank).padStart(2, "0")}</span>
            </span>
            <span class="member-line">
              ${renderAvatar(entry.avatarId, entry.nickname)}
              <span class="member-copy">
                <span class="name-line" title="${escapeHtml(entry.nickname)}">
                  <span class="nickname">${escapeHtml(entry.nickname)}</span>
                  ${isCurrent ? '<span class="you-badge">你</span>' : ""}
                </span>
                ${
                  isMostP
                    ? '<span class="p-champion-badge">最P之人</span>'
                    : `<span class="tendency tone-text-${scoreTone(entry.score)}">${entry.tendency}</span>`
                }
              </span>
            </span>
          </button>
          <div class="leader-card-spectrum">${renderSpectrum(entry.score, true)}</div>
        </article>
      `;
    })
    .join("");

  return `<div class="leader-list">${cards}</div>`;
}

function renderLeaderboardContent(
  entries: readonly LeaderboardEntry[],
  currentEntry: LeaderboardEntry | null,
  preview = false,
): string {
  if (state.leaderboardLoading) {
    return `<div class="leader-list" aria-label="排行榜加载中">${renderLoadingCards()}</div>`;
  }

  if (state.leaderboardError) {
    return `
      <div class="empty-state" role="alert">
        <p>${escapeHtml(state.leaderboardError)}</p>
        <button class="button button-secondary" type="button" data-action="retry-leaderboard">
          <i data-lucide="refresh-cw" aria-hidden="true"></i><span>重新加载</span>
        </button>
      </div>
    `;
  }

  if (entries.length === 0) {
    return `
      <div class="empty-state">
        <i data-lucide="users-round" aria-hidden="true"></i>
        <p>还没有人上榜</p>
        <span>第一个位置留给你。</span>
      </div>
    `;
  }

  const visibleEntries = preview ? entries.slice(0, 5) : entries;
  return `
    ${renderLeaderboardList(visibleEntries, currentEntry)}
    ${
      preview && entries.length > 5
        ? `<button class="text-button" type="button" data-action="leaderboard">查看全部 ${entries.length} 人 <i data-lucide="arrow-right" aria-hidden="true"></i></button>`
        : ""
    }
  `;
}

function renderMemberDialog(): string {
  const member = state.selectedMember;
  if (!member) return "";
  const percentages = scoreToPercentages(member.score);
  return `
    <dialog class="member-dialog" data-member-dialog aria-labelledby="member-dialog-title">
      <button class="dialog-close" type="button" data-action="close-member" aria-label="关闭" title="关闭">
        <i data-lucide="x" aria-hidden="true"></i>
      </button>
      <div class="member-dialog-person">
        ${renderAvatar(member.avatarId, member.nickname, "dialog-avatar")}
        <div>
          <p class="eyebrow">第 ${member.rank} 名</p>
          <h2 id="member-dialog-title">${escapeHtml(member.nickname)}</h2>
        </div>
      </div>
      <p class="member-dialog-tendency">${escapeHtml(member.tendency)}</p>
      <div class="percent-spectrum" aria-label="P ${percentages.p}%，J ${percentages.j}%">
        <span>P ${percentages.p}</span><span>J ${percentages.j}</span>
      </div>
      ${renderSpectrum(member.score)}
    </dialog>
  `;
}

function renderHomeResult(): string {
  const result = state.result;
  if (!result) return "";
  const percentages = scoreToPercentages(result.score);
  const comparison = compareWithAverage(
    result.score,
    averageScore(state.leaderboard.entries),
  );
  return `
    <section class="home-result-band" aria-labelledby="home-result-title">
      <div class="identity-card" data-result-card>
        <div class="result-avatar-area">
          ${
            state.avatarEditorOpen
              ? `${renderAvatarPicker(
                  state.pendingAvatarId ?? result.avatarId ?? DEFAULT_AVATAR_ID,
                  "result",
                )}
                ${
                  state.avatarUpdateError
                    ? `<p class="avatar-update-error" role="alert">${escapeHtml(state.avatarUpdateError)}</p>`
                    : ""
                }
                <div class="avatar-editor-actions">
                  <button class="button button-secondary" type="button" data-action="cancel-avatar-edit" ${state.avatarUpdateSaving ? "disabled" : ""}>
                    <i data-lucide="x" aria-hidden="true"></i><span>取消</span>
                  </button>
                  <button class="button button-primary" type="button" data-action="save-avatar" ${state.avatarUpdateSaving ? "disabled" : ""}>
                    ${
                      state.avatarUpdateSaving
                        ? '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>保存中</span>'
                        : '<i data-lucide="check" aria-hidden="true"></i><span>保存</span>'
                    }
                  </button>
                </div>`
              : `${renderAvatar(result.avatarId, result.nickname, "identity-avatar")}
                <button class="change-avatar-button" type="button" data-action="edit-avatar">
                  <i data-lucide="pencil" aria-hidden="true"></i><span>更换头像</span>
                </button>`
          }
        </div>
        <p class="identity-owner">${escapeHtml(result.nickname)} 是群里的</p>
        <h2 id="home-result-title" class="identity-title view-title" tabindex="-1">「${identityTitle(result.score)}」</h2>
        <div class="percent-spectrum" aria-label="P ${percentages.p}%，J ${percentages.j}%">
          <span>P ${percentages.p}</span><span>J ${percentages.j}</span>
        </div>
        ${renderSpectrum(result.score)}
        <p class="average-comparison">${comparison}</p>
      </div>
      <div class="identity-actions">
        <button class="button button-primary" type="button" data-action="share-result">
          <i data-lucide="share-2" aria-hidden="true"></i><span>分享结果卡</span>
        </button>
        <button class="button button-secondary" type="button" data-action="save-result">
          <i data-lucide="download" aria-hidden="true"></i><span>保存图片</span>
        </button>
      </div>
    </section>
  `;
}

function renderHome(): string {
  const completedDraft = answersComplete() && state.nickname.trim().length > 0;
  const nicknameError = validateNickname(state.nickname);
  return renderShell(`
    <section class="home-hero">
      <div class="home-hero-inner">
        <div class="home-brand-lockup">
          <h1 class="home-brand-title view-title" tabindex="-1">
            <span class="home-brand-name">P人计算器</span>
          </h1>
          <p class="home-brand-credit">design by 小羊</p>
        </div>
      </div>
    </section>

    ${
      state.result
        ? renderHomeResult()
        : `<section class="home-entry-band">
            <div class="home-entry-inner">
              <form class="start-form" data-form="start" novalidate>
                ${renderAvatarPicker()}
                <label for="nickname">你的群昵称</label>
                <input
                  id="nickname"
                  name="nickname"
                  data-testid="nickname-input"
                  type="text"
                  maxlength="5"
                  autocomplete="nickname"
                  placeholder="输入大家认识的昵称"
                  value="${escapeHtml(state.nickname)}"
                  aria-describedby="nickname-help ${state.homeError ? "nickname-error" : ""}"
                  ${state.homeError ? 'aria-invalid="true"' : ""}
                />
                <p id="nickname-help" class="field-help">1–5 个字符，提交后不可修改</p>
                ${
                  state.homeError
                    ? `<p id="nickname-error" class="field-error" role="alert">${escapeHtml(state.homeError)}</p>`
                    : ""
                }
                <button class="button button-primary button-wide home-start-button" type="submit" data-testid="start-button" ${nicknameError ? "disabled" : ""}>
                  <span>${completedDraft ? "继续提交" : "开始答题"}</span><i data-lucide="arrow-right" aria-hidden="true"></i>
                </button>
              </form>
            </div>
          </section>`
    }

  `);
}

function renderQuiz(): string {
  const questionId = state.questionIds[state.questionIndex];
  const question = questionId === undefined ? undefined : getQuestionById(questionId);
  if (!question) return renderHome();
  const selected = state.answers[state.questionIndex];
  const progressPercent = Math.round(
    ((state.questionIndex + 1) / QUIZ_LENGTH) * 100,
  );
  return renderShell(`
    <section class="quiz-shell quiz-tone-${state.questionIndex % 5}">
      <div class="quiz-meta">
        <button class="back-link" type="button" data-action="home">
          <i data-lucide="chevron-left" aria-hidden="true"></i><span>${escapeHtml(state.nickname)}</span>
        </button>
        <span class="question-counter" aria-label="第 ${state.questionIndex + 1} 题，共 ${QUIZ_LENGTH} 题，完成 ${progressPercent}%">
          <span aria-hidden="true">${state.questionIndex + 1}/${QUIZ_LENGTH}</span>
        </span>
      </div>
      <div class="quiz-progress-copy" aria-hidden="true"><span>答题进度</span><strong>${progressPercent}%</strong></div>
      <progress class="progress-track" aria-label="答题进度" max="${QUIZ_LENGTH}" value="${state.questionIndex + 1}"></progress>

      <div class="quiz-tool">
        <fieldset>
          <legend class="question-title view-title" tabindex="-1">${escapeHtml(question.scene)}</legend>
          <div class="question-sides">
            <div class="side side-left"><span>左边</span><p>${escapeHtml(question.left)}</p></div>
            <div class="side side-right"><span>右边</span><p>${escapeHtml(question.right)}</p></div>
          </div>
          <div class="likert-block">
            <div class="likert-scale" role="radiogroup" aria-label="选择更同意哪一种做法">
            ${CHOICE_LABELS.map(
              (label, index) => `
                <label class="likert-choice ${selected === index ? "likert-choice-selected" : ""}">
                  <input
                    type="radio"
                    name="answer"
                    value="${index}"
                    data-testid="choice-${index}"
                    aria-label="${label}"
                    ${selected === index ? "checked" : ""}
                  />
                  <span class="likert-dot" aria-hidden="true"></span>
                </label>
              `,
            ).join("")}
            </div>
            <div class="likert-labels" aria-hidden="true">
              <span>更同意左边</span><span>都可以</span><span>更同意右边</span>
            </div>
          </div>
        </fieldset>

        ${
          state.questionIndex > 0
            ? `<div class="quiz-actions sticky-quiz-actions">
                <button class="back-link quiz-previous" type="button" data-action="previous">
                  <i data-lucide="arrow-left" aria-hidden="true"></i><span>上一题</span>
                </button>
              </div>`
            : `<div class="quiz-actions sticky-quiz-actions">
                <button class="back-link quiz-previous" type="button" disabled>
                  <i data-lucide="arrow-left" aria-hidden="true"></i><span>上一题</span>
                </button>
              </div>`
        }
      </div>
    </section>
  `);
}

function renderConfirm(): string {
  return renderShell(`
    <section class="confirm-shell">
      <div class="confirm-mark" aria-hidden="true"><i data-lucide="check"></i></div>
      <p class="eyebrow">16 / 16 已完成</p>
      <h1 class="display-title view-title" tabindex="-1">准备好上榜了吗？</h1>
      <div class="confirm-summary">
        ${renderAvatar(state.avatarId, state.nickname, "confirm-avatar")}
        <span class="confirm-person">
          <small>群昵称</small>
          <strong>${escapeHtml(state.nickname)}</strong>
        </span>
      </div>
      <p class="confirm-note">提交后不能修改答案或昵称。</p>
      ${
        state.submitError
          ? `<p class="submit-error" role="alert">${escapeHtml(state.submitError)}</p>`
          : ""
      }
      <div class="confirm-actions">
        <button class="button button-secondary" type="button" data-action="back-to-quiz" ${state.submitting ? "disabled" : ""}>
          <i data-lucide="arrow-left" aria-hidden="true"></i><span>返回检查</span>
        </button>
        <button class="button button-primary" type="button" data-action="submit" data-testid="submit-button" ${state.submitting ? "disabled" : ""}>
          ${
            state.submitting
              ? '<i class="spin" data-lucide="loader-circle" aria-hidden="true"></i><span>正在提交</span>'
              : '<span>确认提交</span><i data-lucide="arrow-right" aria-hidden="true"></i>'
          }
        </button>
      </div>
    </section>
  `);
}

function renderResult(): string {
  return renderHome();
}

function renderLeaderboard(): string {
  return renderShell(`
    <section class="standalone-leaderboard leaderboard-section">
      <div class="section-heading section-heading-large">
        <div>
          <button class="back-link" type="button" data-action="return">
            <i data-lucide="chevron-left" aria-hidden="true"></i><span>返回</span>
          </button>
          <p class="eyebrow">共 ${state.leaderboard.total} 人</p>
          <h1 class="display-title view-title" tabindex="-1">群友排行榜</h1>
        </div>
        ${renderIconButton("refresh-cw", "刷新排行榜", "retry-leaderboard")}
      </div>
      ${renderGroupSpectrum(state.leaderboard.entries)}
      ${renderLeaderboardContent(state.leaderboard.entries, state.result)}
    </section>
    ${renderMemberDialog()}
  `);
}

function renderQuestionBankItem(
  question: (typeof QUESTIONS)[number],
  visible: boolean,
): string {
  return `
    <article class="question-bank-item" data-question-id="${question.id}" aria-labelledby="question-bank-title-${question.id}" ${visible ? "" : "hidden"}>
      <span class="question-bank-number">第 ${String(question.id).padStart(3, "0")} 题</span>
      <h2 id="question-bank-title-${question.id}">${escapeHtml(question.scene)}</h2>
      <div class="question-bank-sides">
        <div class="question-bank-side question-bank-side-left">
          <span>左边</span>
          <p>${escapeHtml(question.left)}</p>
        </div>
        <div class="question-bank-side question-bank-side-right">
          <span>右边</span>
          <p>${escapeHtml(question.right)}</p>
        </div>
      </div>
    </article>
  `;
}

function renderQuestionBank(): string {
  const visibleQuestions = filterQuestions(QUESTIONS, state.questionSearch);
  const visibleIds = new Set(visibleQuestions.map(({ id }) => id));
  const hasQuery = normalizeQuestionQuery(state.questionSearch).length > 0;
  return renderShell(`
    <section class="question-bank-section">
      <div class="section-heading section-heading-large question-bank-heading">
        <div>
          <button class="back-link" type="button" data-action="return">
            <i data-lucide="chevron-left" aria-hidden="true"></i><span>返回</span>
          </button>
          <p class="eyebrow">共 ${QUESTIONS.length} 题</p>
          <h1 class="display-title view-title" tabindex="-1">题库</h1>
        </div>
      </div>
      <div class="question-search-block">
        <label for="question-search">搜索题目</label>
        <div class="question-search-control">
          <i data-lucide="search" aria-hidden="true"></i>
          <input
            id="question-search"
            name="question-search"
            type="search"
            autocomplete="off"
            placeholder="输入题号、题目或人物"
            value="${escapeHtml(state.questionSearch)}"
            aria-describedby="question-search-status"
          />
          <button type="button" data-action="clear-question-search" aria-label="清空搜索" title="清空搜索" ${hasQuery ? "" : "hidden"}>
            <i data-lucide="x" aria-hidden="true"></i>
          </button>
        </div>
        <p id="question-search-status" class="question-search-status" aria-live="polite">${
          hasQuery ? `找到 ${visibleQuestions.length} 道题` : `显示全部 ${QUESTIONS.length} 道题`
        }</p>
      </div>
      <div class="question-bank-list">
        ${QUESTIONS.map((question) =>
          renderQuestionBankItem(question, visibleIds.has(question.id)),
        ).join("")}
      </div>
      <div class="empty-state question-bank-empty" ${visibleQuestions.length === 0 ? "" : "hidden"}>
        <i data-lucide="search" aria-hidden="true"></i>
        <p>没有找到相关题目</p>
        <span>换一个题号或关键词试试。</span>
      </div>
    </section>
  `);
}

function applyQuestionSearch(value: string): void {
  state.questionSearch = value;
  const visibleIds = new Set(
    filterQuestions(QUESTIONS, value).map(({ id }) => id),
  );
  const hasQuery = normalizeQuestionQuery(value).length > 0;

  document
    .querySelectorAll<HTMLElement>("[data-question-id]")
    .forEach((item) => {
      item.hidden = !visibleIds.has(Number(item.dataset.questionId));
    });

  const status = document.querySelector<HTMLElement>("#question-search-status");
  if (status) {
    status.textContent = hasQuery
      ? `找到 ${visibleIds.size} 道题`
      : `显示全部 ${QUESTIONS.length} 道题`;
  }
  const emptyState = document.querySelector<HTMLElement>(".question-bank-empty");
  if (emptyState) emptyState.hidden = visibleIds.size > 0;
  const clearButton = document.querySelector<HTMLButtonElement>(
    '[data-action="clear-question-search"]',
  );
  if (clearButton) clearButton.hidden = !hasQuery;
}

function render(): void {
  const content =
    state.view === "quiz"
      ? renderQuiz()
      : state.view === "confirm"
        ? renderConfirm()
        : state.view === "result"
          ? renderResult()
          : state.view === "leaderboard"
            ? renderLeaderboard()
            : state.view === "questionBank"
              ? renderQuestionBank()
            : renderHome();
  app.innerHTML = content;
  createIcons({
    icons: {
      ArrowLeft,
      ArrowRight,
      BookOpen,
      Check,
      ChevronLeft,
      ChevronRight,
      Crown,
      Download,
      LoaderCircle,
      MoveHorizontal,
      Pencil,
      RefreshCw,
      Search,
      Share2,
      Trophy,
      UserRound,
      UsersRound,
      X,
    },
    attrs: { "stroke-width": 1.8 },
  });
  if (state.view === "home") {
    requestAnimationFrame(centerSelectedAvatars);
    if (showAvatarHint && avatarHintTimer === undefined) {
      avatarHintTimer = window.setTimeout(() => {
        avatarHintTimer = undefined;
        dismissAvatarHint();
      }, 1700);
    }
  }
  const memberDialog = document.querySelector<HTMLDialogElement>(
    "[data-member-dialog]",
  );
  if (memberDialog && !memberDialog.open) {
    memberDialog.showModal();
  }
}

function centerSelectedAvatar(carousel?: HTMLElement | null): void {
  const target =
    carousel ?? document.querySelector<HTMLElement>("[data-avatar-carousel]");
  if (!target) return;
  const selected = target
    .querySelector<HTMLInputElement>('input[type="radio"]:checked')
    ?.closest<HTMLElement>(".avatar-option");
  if (!selected) return;
  const carouselRect = target.getBoundingClientRect();
  const selectedRect = selected.getBoundingClientRect();
  target.scrollTo({
    left:
      target.scrollLeft +
      selectedRect.left +
      selectedRect.width / 2 -
      (carouselRect.left + carouselRect.width / 2),
    behavior: "auto",
  });
}

function centerSelectedAvatars(): void {
  document
    .querySelectorAll<HTMLElement>("[data-avatar-carousel]")
    .forEach((carousel) => centerSelectedAvatar(carousel));
}

function commitAvatarSelection(
  avatarId: AvatarId,
  scope: AvatarScope = "draft",
): void {
  if (scope === "result") {
    if (state.pendingAvatarId === avatarId) return;
    state.pendingAvatarId = avatarId;
  } else {
    if (state.avatarId === avatarId) return;
    state.avatarId = avatarId;
    persistDraft();
  }
  const input = document.querySelector<HTMLInputElement>(
    `input[name="${scope}-avatar"][value="${avatarId}"]`,
  );
  if (input) input.checked = true;
}

function dismissAvatarHint(): void {
  if (!showAvatarHint) return;
  showAvatarHint = false;
  localStorage.setItem(AVATAR_HINT_KEY, "true");
  document.querySelector("[data-avatar-hint]")?.remove();
}

function selectAvatarByOffset(offset: number, scope: AvatarScope): void {
  const selectedAvatarId =
    scope === "result"
      ? (state.pendingAvatarId ?? state.result?.avatarId ?? DEFAULT_AVATAR_ID)
      : state.avatarId;
  const currentIndex = AVATARS.findIndex(
    (avatar) => avatar.id === selectedAvatarId,
  );
  const nextIndex = (currentIndex + offset + AVATARS.length) % AVATARS.length;
  const avatar = AVATARS[nextIndex];
  if (!avatar) return;
  commitAvatarSelection(avatar.id, scope);
  centerSelectedAvatar(
    document.querySelector<HTMLElement>(
      `[data-avatar-carousel][data-avatar-scope="${scope}"]`,
    ),
  );
}

function selectCenteredAvatar(carousel: HTMLElement): void {
  const carouselRect = carousel.getBoundingClientRect();
  const center = carouselRect.left + carouselRect.width / 2;
  const options = Array.from(
    carousel.querySelectorAll<HTMLElement>(".avatar-option"),
  );
  const closest = options.reduce<HTMLElement | null>((nearest, option) => {
    if (!nearest) return option;
    const optionRect = option.getBoundingClientRect();
    const nearestRect = nearest.getBoundingClientRect();
    const optionDistance = Math.abs(optionRect.left + optionRect.width / 2 - center);
    const nearestDistance = Math.abs(
      nearestRect.left + nearestRect.width / 2 - center,
    );
    return optionDistance < nearestDistance ? option : nearest;
  }, null);
  const avatarId = closest?.dataset.avatarId;
  if (!validateAvatarId(avatarId)) return;
  commitAvatarSelection(
    avatarId,
    carousel.dataset.avatarScope === "result" ? "result" : "draft",
  );
}

function focusView(): void {
  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>(".view-title")?.focus({ preventScroll: true });
  });
}

function cancelAutoAdvance(): void {
  if (autoAdvanceTimer === undefined) return;
  window.clearTimeout(autoAdvanceTimer);
  autoAdvanceTimer = undefined;
}

function setView(view: View, focus = true): void {
  cancelAutoAdvance();
  if (view !== "leaderboard") state.selectedMember = null;
  state.view = view;
  window.scrollTo({ top: 0, behavior: "instant" });
  render();
  if (focus) focusView();
}

function showHomeResult(): void {
  setView("home");
}

async function refreshLeaderboard(): Promise<void> {
  state.leaderboardLoading = true;
  state.leaderboardError = "";
  render();
  try {
    state.leaderboard = await getLeaderboard();
    if (!state.result) {
      try {
        const lastResult = readLastResultAccess();
        state.result =
          state.leaderboard.entries.find(
            (entry) =>
              entry.nickname === lastResult?.nickname &&
              entry.submittedAt === lastResult?.submittedAt,
          ) ?? null;
        state.resultEditToken = state.result ? lastResult?.editToken ?? null : null;
      } catch {
        state.resultEditToken = null;
      }
    }
  } catch (caught) {
    state.leaderboardError =
      caught instanceof Error ? caught.message : "排行榜暂时加载失败";
  } finally {
    state.leaderboardLoading = false;
    render();
  }
}

function validateNickname(value: string): string {
  const normalized = value.normalize("NFKC").trim().replace(/\s+/gu, " ");
  if (Array.from(normalized).length === 0) return "请输入群昵称";
  if (Array.from(normalized).length > 5) return "昵称最多 5 个字符";
  return "";
}

async function submitCurrentAnswers(): Promise<void> {
  const answers = state.answers.filter((answer): answer is number => answer !== null);
  if (
    !validateQuestionSet(state.questionIds) ||
    answers.length !== QUIZ_LENGTH ||
    state.submitting
  ) {
    return;
  }

  state.submitting = true;
  state.submitError = "";
  render();

  try {
    const response: SubmitResponse = await submitAnswers(
      state.nickname,
      state.avatarId,
      state.questionIds,
      answers,
    );
    state.result = response.result;
    state.leaderboard = response.leaderboard;
    state.leaderboardLoading = false;
    state.resultEditToken = response.editToken;
    persistLastResultAccess(response.result, response.editToken);
    clearDraft();
    state.nickname = "";
    state.avatarId = DEFAULT_AVATAR_ID;
    state.questionIds = [];
    state.answers = emptyAnswers();
    state.questionIndex = 0;
    state.submitting = false;
    showHomeResult();
  } catch (caught) {
    if (caught instanceof ApiError && caught.code === "NICKNAME_TAKEN") {
      state.homeError = caught.message;
      setView("home");
      requestAnimationFrame(() => {
        document.querySelector<HTMLInputElement>("#nickname")?.select();
      });
    } else {
      state.submitError =
        caught instanceof Error ? caught.message : "暂时无法提交，请稍后重试";
      render();
    }
  } finally {
    state.submitting = false;
  }
}

async function saveAvatarChange(): Promise<void> {
  const result = state.result;
  const avatarId = state.pendingAvatarId;
  if (!result || !avatarId || state.avatarUpdateSaving) return;

  if (avatarId === result.avatarId) {
    state.avatarEditorOpen = false;
    state.avatarUpdateError = "";
    render();
    return;
  }

  state.avatarUpdateSaving = true;
  state.avatarUpdateError = "";
  render();
  try {
    const response = await updateAvatar(
      result.nickname,
      result.submittedAt,
      avatarId,
      state.resultEditToken,
    );
    state.result = response.result;
    state.leaderboard = response.leaderboard;
    state.resultEditToken = response.editToken;
    state.avatarEditorOpen = false;
    state.pendingAvatarId = null;
    persistLastResultAccess(response.result, response.editToken);
    state.avatarUpdateSaving = false;
    showNotice("头像已更新");
  } catch (caught) {
    state.avatarUpdateError =
      caught instanceof Error ? caught.message : "头像暂时无法更新";
    state.avatarUpdateSaving = false;
    render();
  }
}

async function sharePage(): Promise<void> {
  const shareData = {
    title: "花儿与少年（pj版）",
    text: "你会提前定下来，还是边走边看？来群友 P/J 光谱上找位置。",
    url: window.location.origin,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }
    await navigator.clipboard.writeText(shareData.url);
    state.shareNotice = "链接已复制";
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === "AbortError") return;
    state.shareNotice = "复制失败，请从地址栏复制链接";
  }
  render();
  window.setTimeout(() => {
    state.shareNotice = "";
    render();
  }, 2200);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("头像加载失败"));
    image.src = src;
  });
}

async function createResultCardBlob(): Promise<Blob> {
  const result = state.result;
  if (!result) throw new Error("没有可保存的结果");
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法生成图片");

  const percentages = scoreToPercentages(result.score);
  const comparison = compareWithAverage(
    result.score,
    averageScore(state.leaderboard.entries),
  );
  context.fillStyle = "#faf5e9";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#eaf7ff";
  context.fillRect(0, 0, canvas.width, 190);
  context.fillStyle = "#f5d761";
  context.fillRect(0, 184, canvas.width, 12);
  context.fillStyle = "#171717";
  context.font = '700 42px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.textAlign = "center";
  context.fillText("花儿与少年（pj版）", 540, 112);

  const avatar = getAvatarById(result.avatarId);
  if (avatar) {
    const image = await loadImage(avatar.src);
    context.drawImage(image, 350, 230, 380, 380);
  }

  context.fillStyle = "#56514a";
  context.font = '500 32px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.fillText(`${result.nickname} 是群里的`, 540, 665);
  context.fillStyle = "#171717";
  context.font = '800 58px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.fillText(`「${identityTitle(result.score)}」`, 540, 750);

  context.font = '800 40px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.fillStyle = "#496e9d";
  context.textAlign = "left";
  context.fillText(`P ${percentages.p}`, 130, 875);
  context.fillStyle = "#a64272";
  context.textAlign = "right";
  context.fillText(`J ${percentages.j}`, 950, 875);
  context.strokeStyle = "#d9d2c5";
  context.lineWidth = 10;
  context.beginPath();
  context.moveTo(140, 940);
  context.lineTo(940, 940);
  context.stroke();
  const markerX = 140 + ((result.score + 100) / 200) * 800;
  context.fillStyle = scoreTone(result.score) === "p" ? "#496e9d" : scoreTone(result.score) === "j" ? "#a64272" : "#806900";
  context.beginPath();
  context.arc(markerX, 940, 22, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = "#fffdf7";
  context.lineWidth = 8;
  context.stroke();

  context.fillStyle = "#32312e";
  context.font = '600 34px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.textAlign = "center";
  context.fillText(comparison, 540, 1055);
  context.fillStyle = "#68645d";
  context.font = '500 26px "PingFang SC", "Microsoft YaHei", sans-serif';
  context.fillText("群聊娱乐测试，不代表正式 MBTI 结果", 540, 1240);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("图片生成失败"));
    }, "image/png");
  });
}

function downloadBlob(blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "花儿与少年-pj结果卡.png";
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function showNotice(message: string): void {
  state.shareNotice = message;
  render();
  window.setTimeout(() => {
    state.shareNotice = "";
    render();
  }, 2200);
}

async function saveResultCard(): Promise<void> {
  try {
    downloadBlob(await createResultCardBlob());
    showNotice("结果卡已生成");
  } catch (caught) {
    showNotice(caught instanceof Error ? caught.message : "图片生成失败");
  }
}

async function shareResultCard(): Promise<void> {
  const result = state.result;
  if (!result) return;
  try {
    const blob = await createResultCardBlob();
    const file = new File([blob], "pj-result.png", { type: "image/png" });
    if (navigator.share && navigator.canShare?.({ files: [file] })) {
      await navigator.share({
        title: "花儿与少年（pj版）",
        text: `${result.nickname} 是群里的「${identityTitle(result.score)}」`,
        files: [file],
      });
      return;
    }
    downloadBlob(blob);
    showNotice("浏览器不支持图片分享，已生成图片");
  } catch (caught) {
    if (caught instanceof DOMException && caught.name === "AbortError") return;
    showNotice(caught instanceof Error ? caught.message : "分享失败");
  }
}

app.addEventListener("submit", (event) => {
  event.preventDefault();
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;

  if (form.dataset.form === "start") {
    const input = form.elements.namedItem("nickname");
    if (!(input instanceof HTMLInputElement)) return;
    state.nickname = input.value.normalize("NFKC").trim().replace(/\s+/gu, " ");
    state.homeError = validateNickname(state.nickname);
    if (state.homeError) {
      render();
      requestAnimationFrame(() =>
        document.querySelector<HTMLInputElement>("#nickname")?.focus(),
      );
      return;
    }
    state.result = null;
    if (!validateQuestionSet(state.questionIds)) {
      state.questionIds = createQuestionSet();
      state.answers = emptyAnswers();
      state.questionIndex = 0;
    }
    persistDraft();
    setView(answersComplete() ? "confirm" : "quiz");
  }
});

app.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.name.endsWith("-avatar") && validateAvatarId(target.value)) {
    const scope: AvatarScope =
      target.name === "result-avatar" ? "result" : "draft";
    commitAvatarSelection(target.value, scope);
    requestAnimationFrame(centerSelectedAvatars);
    return;
  }
  if (target.name !== "answer") return;
  const questionIndex = state.questionIndex;
  const answer = Number(target.value);
  cancelAutoAdvance();
  state.answers[questionIndex] = answer;
  persistDraft();

  const delay = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ? 0
    : AUTO_ADVANCE_DELAY_MS;
  autoAdvanceTimer = window.setTimeout(() => {
    autoAdvanceTimer = undefined;
    if (
      state.view !== "quiz" ||
      state.questionIndex !== questionIndex ||
      state.answers[questionIndex] !== answer
    ) {
      return;
    }
    if (questionIndex === QUIZ_LENGTH - 1) {
      setView("confirm");
      return;
    }
    state.questionIndex = questionIndex + 1;
    persistDraft();
    setView("quiz");
  }, delay);
});

app.addEventListener("input", (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (target.name === "question-search") {
    applyQuestionSearch(target.value);
    return;
  }
  if (target.name !== "nickname") return;
  state.nickname = target.value;
  if (state.homeError) {
    state.homeError = "";
    target.removeAttribute("aria-invalid");
    document.querySelector("#nickname-error")?.remove();
  }
  const startButton = document.querySelector<HTMLButtonElement>(
    "[data-testid='start-button']",
  );
  if (startButton) startButton.disabled = Boolean(validateNickname(state.nickname));
});

app.addEventListener("pointerdown", (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.closest("[data-avatar-carousel]")) {
    return;
  }
  dismissAvatarHint();
  avatarGestureActive = event.pointerType === "touch";
});

app.addEventListener(
  "scroll",
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.matches("[data-avatar-carousel]")) {
      return;
    }
    if (avatarScrollFrame !== undefined) return;
    avatarScrollFrame = requestAnimationFrame(() => {
      avatarScrollFrame = undefined;
      if (!target.isConnected) return;
      selectCenteredAvatar(target);
    });
    if (avatarGestureActive) {
      if (avatarHapticTimer !== undefined) {
        window.clearTimeout(avatarHapticTimer);
      }
      avatarHapticTimer = window.setTimeout(() => {
        avatarHapticTimer = undefined;
        avatarGestureActive = false;
        if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
          navigator.vibrate?.(8);
        }
      }, 120);
    }
  },
  true,
);

app.addEventListener("cancel", (event) => {
  if (!(event.target instanceof HTMLDialogElement)) return;
  event.preventDefault();
  state.selectedMember = null;
  render();
});

app.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest<HTMLButtonElement>("[data-action]");
  if (!button || button.disabled) return;
  const action = button.dataset.action;
  cancelAutoAdvance();

  if (action === "home") {
    if (state.view === "result" || (state.view === "home" && state.result)) {
      resetForNextPerson();
    }
    setView("home");
  } else if (action === "leaderboard") {
    state.returnView = state.view;
    setView("leaderboard");
  } else if (action === "question-bank") {
    state.returnView = state.view;
    setView("questionBank");
  } else if (action === "return") {
    setView(state.returnView === state.view ? "home" : state.returnView);
  } else if (action === "share") {
    void sharePage();
  } else if (action === "share-result") {
    void shareResultCard();
  } else if (action === "save-result") {
    void saveResultCard();
  } else if (action === "retry-leaderboard") {
    void refreshLeaderboard();
  } else if (action === "avatar-previous") {
    dismissAvatarHint();
    selectAvatarByOffset(
      -1,
      button.dataset.avatarScope === "result" ? "result" : "draft",
    );
  } else if (action === "avatar-next") {
    dismissAvatarHint();
    selectAvatarByOffset(
      1,
      button.dataset.avatarScope === "result" ? "result" : "draft",
    );
  } else if (action === "edit-avatar") {
    if (!state.result) return;
    state.pendingAvatarId = state.result.avatarId ?? DEFAULT_AVATAR_ID;
    state.avatarEditorOpen = true;
    state.avatarUpdateError = "";
    render();
  } else if (action === "cancel-avatar-edit") {
    state.avatarEditorOpen = false;
    state.pendingAvatarId = null;
    state.avatarUpdateError = "";
    render();
  } else if (action === "save-avatar") {
    void saveAvatarChange();
  } else if (action === "previous") {
    state.questionIndex = Math.max(0, state.questionIndex - 1);
    persistDraft();
    setView("quiz");
  } else if (action === "back-to-quiz") {
    state.questionIndex = QUIZ_LENGTH - 1;
    setView("quiz");
  } else if (action === "submit") {
    void submitCurrentAnswers();
  } else if (action === "member-result") {
    const entryIndex = Number(button.dataset.entryIndex);
    state.selectedMember = state.leaderboard.entries[entryIndex] ?? null;
    render();
  } else if (action === "close-member") {
    state.selectedMember = null;
    render();
  } else if (action === "clear-question-search") {
    state.questionSearch = "";
    render();
    requestAnimationFrame(() =>
      document.querySelector<HTMLInputElement>("#question-search")?.focus(),
    );
  }
});

render();
void refreshLeaderboard();
